import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Minus, Search, X, Printer, User, Percent, Wallet, ScanLine, Grid3x3, Zap, PauseCircle, ListChecks, MessageCircle } from "lucide-react";
import { shareInvoiceWhatsApp } from "./invoicePdf";
import api from "../lib/api";
import { inr } from "../lib/api";
import PrintableBill from "./PrintableBill";

const PAYMENT_MODES = ["cash", "upi", "card"];

export default function PosTerminal() {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [cart, setCart] = useState([]);              // [{product_id, name, sku, size, color, qty, unit_price, image, line_discount}]
  const [customer, setCustomer] = useState(null);    // {id, name, phone} or null
  const [walkin, setWalkin] = useState({ name: "", phone: "" });
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState(0);
  const [coupon, setCoupon] = useState("");
  const [gstEnabled, setGstEnabled] = useState(false);
  const [gstRate, setGstRate] = useState(5);
  const [gstin, setGstin] = useState("");
  const [payments, setPayments] = useState([{ mode: "cash", amount: 0 }]);
  const [notes, setNotes] = useState("");
  const [savedBill, setSavedBill] = useState(null);
  const [showPrintable, setShowPrintable] = useState(false);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseList, setBrowseList] = useState([]);
  const [browseQuery, setBrowseQuery] = useState("");
  const [browseLoading, setBrowseLoading] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quick, setQuick] = useState({ name: "", price: "", qty: 1 });
  const [parked, setParked] = useState([]);
  const [parkedOpen, setParkedOpen] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [queued, setQueued] = useState(0);
  const searchRef = useRef(null);

  const reloadHeld = () => api.get("/pos/held").then((r) => setParked(r.data || [])).catch(() => {});
  useEffect(() => { reloadHeld(); }, []);

  // Load saved customers once for name suggestions while typing a walk-in name.
  const loadCustomers = () => api.get("/pos/customers").then((r) => setAllCustomers(r.data || [])).catch(() => {});
  useEffect(() => { loadCustomers(); }, []);

  // Pick a saved customer's phone automatically when the typed name matches one.
  const onWalkinName = (e) => {
    const name = e.target.value;
    const match = allCustomers.find((c) => (c.name || "").toLowerCase() === name.trim().toLowerCase());
    setWalkin((w) => ({ ...w, name, ...(match && !w.phone ? { phone: match.phone || "" } : {}) }));
  };

  useEffect(() => { api.get("/pos/settings").then((r) => setSettings(r.data || {})); }, []);
  useEffect(() => { searchRef.current?.focus(); }, []);

  // ---- Offline resilience: queue bills when the network drops, flush on reconnect ----
  const readQueue = () => { try { return JSON.parse(localStorage.getItem("ambrai_pos_offline_queue") || "[]"); } catch { return []; } };
  const writeQueue = (q) => { localStorage.setItem("ambrai_pos_offline_queue", JSON.stringify(q)); setQueued(q.length); };
  useEffect(() => { setQueued(readQueue().length); }, []);
  const flushQueue = async () => {
    const q = readQueue();
    if (!q.length) return;
    const remaining = [];
    for (const p of q) { try { await api.post("/pos/bills", p); } catch { remaining.push(p); } }
    writeQueue(remaining);
    if (q.length && !remaining.length) toast.success(`${q.length} offline bill(s) synced`);
  };
  useEffect(() => {
    const on = () => { setOnline(true); flushQueue(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
    // eslint-disable-next-line
  }, []);

  const openBrowse = async () => {
    setBrowseOpen(true);
    setBrowseLoading(true);
    try {
      // Always refetch live so newly uploaded products and current stock show.
      const { data } = await api.get("/admin/products");
      setBrowseList(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      toast.error("Could not load catalogue");
    } finally {
      setBrowseLoading(false);
    }
  };

  const filteredBrowse = useMemo(() => {
    const q = browseQuery.trim().toLowerCase();
    if (!q) return browseList;
    return browseList.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.sku || "").toLowerCase().includes(q) ||
      (p.category || "").toLowerCase().includes(q)
    );
  }, [browseList, browseQuery]);

  const pickFromBrowse = (p) => {
    addToCart(p);
    setBrowseOpen(false);
    setBrowseQuery("");
  };

  const addQuick = () => {
    const price = Number(quick.price) || 0;
    const qty = Math.max(1, Number(quick.qty) || 1);
    if (!quick.name.trim()) return toast.error("Enter item name");
    if (price <= 0) return toast.error("Enter a price");
    setCart((prev) => [...prev, {
      product_id: "", name: quick.name.trim(), sku: "",
      size: "", color: "", qty, unit_price: price,
      image: "", line_discount: 0,
    }]);
    setQuick({ name: "", price: "", qty: 1 });
    setQuickOpen(false);
  };

  // Product search (debounced)
  useEffect(() => {
    if (!query.trim()) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      api.get("/pos/product-search", { params: { q: query } }).then((r) => setSearchResults(r.data));
    }, 220);
    return () => clearTimeout(t);
  }, [query]);

  // Customer search
  useEffect(() => {
    if (!custQuery.trim()) { setCustResults([]); return; }
    const t = setTimeout(() => {
      api.get("/pos/customers", { params: { q: custQuery } }).then((r) => setCustResults(r.data));
    }, 220);
    return () => clearTimeout(t);
  }, [custQuery]);

  const addToCart = (p, size = "", color = "") => {
    // If the server hinted a size (from a scanned variant barcode like AMB-1005-M), use it
    const chosenSize = size || p._matched_size || (p.sizes?.[0] || "");
    const variantColors = (p.color_variants || []).map((v) => v.color);
    const chosenColor = color || p._matched_color || variantColors[0] || (p.colors?.[0] || p.color || "");
    setCart((prev) => {
      const key = `${p.id}|${chosenSize}|${chosenColor}`;
      const idx = prev.findIndex((x) => `${x.product_id}|${x.size}|${x.color}` === key);
      if (idx >= 0) {
        const clone = [...prev];
        clone[idx] = { ...clone[idx], qty: clone[idx].qty + 1 };
        return clone;
      }
      return [...prev, {
        product_id: p.id, name: p.name, sku: p.sku || "",
        size: chosenSize, color: chosenColor,
        qty: 1, unit_price: p.price, image: p.primary_image, line_discount: 0,
        stock: p.stock, colors: variantColors,
      }];
    });
    setQuery("");
    setSearchResults([]);
    searchRef.current?.focus();
  };

  // Enter key on search: if exact SKU match, auto-add
  const handleSearchKey = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (searchResults.length > 0) { addToCart(searchResults[0]); return; }
    const q = query.trim();
    if (!q) return;
    try {
      const { data } = await api.get("/pos/product-search", { params: { q } });
      if (data && data.length) addToCart(data[0]);
      else toast.error(`No product for "${q}"`);
    } catch { /* ignore */ }
  };

  const updateLine = (i, patch) => setCart((c) => c.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeLine = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const subtotal = useMemo(
    () => cart.reduce((s, i) => s + i.unit_price * i.qty - (i.line_discount || 0), 0),
    [cart]
  );
  const discount = useMemo(() => {
    if (discountType === "flat") return Math.min(discountValue || 0, subtotal);
    if (discountType === "percent") return Math.floor((subtotal * (discountValue || 0)) / 100);
    return 0;
  }, [discountType, discountValue, subtotal]);
  const taxable = Math.max(0, subtotal - discount);
  const cgst = gstEnabled ? Math.floor((taxable * (gstRate / 2)) / 100) : 0;
  const sgst = gstEnabled ? Math.floor((taxable * (gstRate / 2)) / 100) : 0;
  const total = taxable + cgst + sgst;
  const paid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balance = total - paid;

  const setPayLine = (i, patch) => setPayments((p) => p.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addPayLine = () => setPayments((p) => [...p, { mode: "upi", amount: 0 }]);
  const rmPayLine = (i) => setPayments((p) => p.filter((_, idx) => idx !== i));
  const payFull = () => setPayments([{ mode: payments[0]?.mode || "cash", amount: total }]);

  const resetAll = () => {
    setCart([]); setCustomer(null); setWalkin({ name: "", phone: "" });
    setDiscountType("none"); setDiscountValue(0); setCoupon("");
    setGstEnabled(false); setGstRate(5); setGstin("");
    setPayments([{ mode: "cash", amount: 0 }]); setNotes(""); setIsBooking(false);
    setSavedBill(null); setShowPrintable(false);
    searchRef.current?.focus();
  };

  // Clears the working bill inputs but keeps `savedBill` intact so the just-
  // generated bill can still be printed. Called right after a successful save.
  const resetWorkingForm = () => {
    setCart([]); setCustomer(null); setWalkin({ name: "", phone: "" });
    setDiscountType("none"); setDiscountValue(0); setCoupon("");
    setGstEnabled(false); setGstRate(5); setGstin("");
    setPayments([{ mode: "cash", amount: 0 }]); setNotes(""); setIsBooking(false);
    searchRef.current?.focus();
  };

  // ---- Hold / Park a bill so the till is free, resume it later (server-side) ----
  const holdBill = async () => {
    if (cart.length === 0) return toast.error("Nothing to hold");
    try {
      await api.post("/pos/held", {
        label: customer?.name || walkin.name || "Walk-in",
        count: cart.reduce((s, i) => s + i.qty, 0),
        total: cart.reduce((s, i) => s + i.unit_price * i.qty - (i.line_discount || 0), 0),
        state: { cart, customer, walkin, discountType, discountValue, coupon, gstEnabled, gstRate, gstin, payments, notes, isBooking },
      });
      await reloadHeld();
      resetWorkingForm();
      setSavedBill(null);
      toast.success("Bill held — resume it anytime from Parked");
    } catch { toast.error("Could not hold bill"); }
  };

  const resumeBill = async (id) => {
    const p = parked.find((x) => x.id === id);
    if (!p) return;
    const s = p.state || {};
    setCart(s.cart || []); setCustomer(s.customer || null); setWalkin(s.walkin || { name: "", phone: "" });
    setDiscountType(s.discountType || "none"); setDiscountValue(s.discountValue || 0); setCoupon(s.coupon || "");
    setGstEnabled(!!s.gstEnabled); setGstRate(s.gstRate || 5); setGstin(s.gstin || "");
    setPayments(s.payments || [{ mode: "cash", amount: 0 }]); setNotes(s.notes || ""); setIsBooking(!!s.isBooking);
    setSavedBill(null);
    try { await api.delete(`/pos/held/${id}`); } catch { /* ignore */ }
    await reloadHeld();
    setParkedOpen(false);
    toast.success("Resumed held bill");
  };

  const deleteParked = async (id) => { try { await api.delete(`/pos/held/${id}`); } catch { /* ignore */ } reloadHeld(); };


  const saveBill = async () => {
    if (cart.length === 0) return toast.error("Add at least one item");    setSaving(true);
    try {
      const payload = {
        items: cart.map(({ stock, image, ...rest }) => rest),
        customer_id: customer?.id || null,
        walkin_name: walkin.name, walkin_phone: walkin.phone,
        discount_type: discountType, discount_value: Number(discountValue) || 0,
        coupon_code: coupon,
        gst_enabled: gstEnabled, gst_rate: Number(gstRate) || 0, gstin,
        payments: payments.filter((p) => Number(p.amount) > 0).map((p) => ({ ...p, amount: Number(p.amount) })),
        is_booking: isBooking,
        notes,
      };
      // Offline: queue the bill locally and sync automatically when back online.
      if (!navigator.onLine) {
        writeQueue([...readQueue(), payload]);
        toast.warning("Offline — bill queued, will sync when connection returns");
        resetWorkingForm();
        setSaving(false);
        return null;
      }
      const { data } = await api.post("/pos/bills", payload);
      setSavedBill(data);
      // Offer to WhatsApp the invoice right away if we have a phone number.
      if ((data.customer_phone || "").replace(/\D/g, "")) {
        toast.success(`Bill ${data.bill_no} saved`, {
          description: "Send the invoice to the customer on WhatsApp?",
          action: { label: "WhatsApp", onClick: () => waSendTerminal(data) },
          duration: 9000,
        });
      } else {
        toast.success(`Bill ${data.bill_no} saved — ready for the next invoice`);
      }
      // Clear the working form so the terminal is immediately ready for a new
      // bill; savedBill is retained for printing the receipt just generated.
      resetWorkingForm();
      return data;
    } catch (err) {
      // Network drop mid-request: queue it rather than losing the sale.
      if (err.message === "Network Error" || !navigator.onLine) {
        writeQueue([...readQueue(), {
          items: cart.map(({ stock, image, ...rest }) => rest),
          customer_id: customer?.id || null,
          walkin_name: walkin.name, walkin_phone: walkin.phone,
          discount_type: discountType, discount_value: Number(discountValue) || 0,
          coupon_code: coupon, gst_enabled: gstEnabled, gst_rate: Number(gstRate) || 0, gstin,
          payments: payments.filter((p) => Number(p.amount) > 0).map((p) => ({ ...p, amount: Number(p.amount) })),
          is_booking: isBooking, notes,
        }]);
        toast.warning("Connection lost — bill queued, will sync automatically");
        resetWorkingForm();
      } else {
        toast.error(err.response?.data?.detail || "Failed to save bill");
      }
    } finally {
      setSaving(false);
    }
  };

  const saveAndPrint = async () => {
    await saveBill();
    setTimeout(() => setShowPrintable(true), 100);
  };

  // Share the invoice as a PDF via WhatsApp for the customer on the bill.
  const waSendTerminal = (bill) => { shareInvoiceWhatsApp(bill, settings); };

  useEffect(() => {
    if (showPrintable && savedBill) {
      setTimeout(() => { window.print(); setShowPrintable(false); }, 250);
    }
  }, [showPrintable, savedBill]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_460px] min-h-screen" data-testid="pos-terminal">
      {/* LEFT: search + product grid */}
      <div className="p-6 border-r border-slate-800 flex flex-col min-h-screen">
        <div className="mb-5 space-y-3">
          {(!online || queued > 0) && (
            <div className={`flex items-center gap-2 text-xs rounded-md px-3 py-2 ${online ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"}`} data-testid="pos-offline-banner">
              <span className={`h-2 w-2 rounded-full ${online ? "bg-amber-400" : "bg-red-400 animate-pulse"}`} />
              {online
                ? `${queued} bill(s) queued — syncing…`
                : "Offline — bills will be queued on this device and synced automatically when the connection returns."}
            </div>
          )}
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-500" size={18} />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKey}
              placeholder="Scan barcode / type SKU or product name and hit Enter"
              className="w-full bg-slate-900 border border-slate-800 rounded-md pl-11 pr-4 py-3.5 text-slate-100 placeholder:text-slate-500 focus:border-amber-500 outline-none font-mono text-sm"
              data-testid="pos-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
          <button
            onClick={openBrowse}
            className="flex items-center gap-2 px-4 py-3.5 border border-slate-800 rounded-md text-slate-300 hover:text-amber-400 hover:border-amber-500 transition-colors text-sm"
            data-testid="pos-browse"
            title="Browse full product catalogue"
          >
            <Grid3x3 size={15} /> Browse
          </button>
          <button
            onClick={() => setQuickOpen(true)}
            className="flex items-center gap-2 px-4 py-3.5 border border-slate-800 rounded-md text-slate-300 hover:text-amber-400 hover:border-amber-500 transition-colors text-sm"
            data-testid="pos-quick-add"
            title="Sell a temporary / unlisted item"
          >
            <Zap size={15} /> Quick add
          </button>
          <button
            onClick={holdBill}
            disabled={cart.length === 0}
            className="flex items-center gap-2 px-4 py-3.5 border border-slate-800 rounded-md text-slate-300 hover:text-amber-400 hover:border-amber-500 transition-colors text-sm disabled:opacity-40"
            data-testid="pos-hold-bill"
            title="Hold this bill and free up the till"
          >
            <PauseCircle size={15} /> Hold
          </button>
          <button
            onClick={() => setParkedOpen(true)}
            className="relative flex items-center gap-2 px-4 py-3.5 border border-slate-800 rounded-md text-slate-300 hover:text-amber-400 hover:border-amber-500 transition-colors text-sm"
            data-testid="pos-parked-open"
            title="Resume a held bill"
          >
            <ListChecks size={15} /> Parked
            {parked.length > 0 && <span className="ml-1 min-w-5 h-5 grid place-items-center rounded-full bg-amber-500 text-slate-950 text-xs font-bold px-1" data-testid="pos-parked-count">{parked.length}</span>}
          </button>
          <button
            onClick={resetAll}
            className="px-4 py-3.5 border border-slate-800 rounded-md text-slate-400 hover:text-amber-400 hover:border-amber-500 transition-colors text-sm"
            data-testid="pos-new-bill"
          >
            New bill
          </button>
          </div>
        </div>

        {searchResults.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-md divide-y divide-slate-800 mb-5 max-h-80 overflow-y-auto">
            {searchResults.map((p) => (
              <button
                key={p.id}
                onClick={() => addToCart(p)}
                className="w-full flex items-center gap-4 p-3 hover:bg-slate-800 text-left"
                data-testid={`pos-search-result-${p.sku || p.slug}`}
              >
                <img src={p.primary_image} alt="" className="h-12 w-12 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-100 truncate">{p.name}</p>
                  <p className="text-xs text-slate-500 font-mono">SKU {p.sku || "—"} · Stock {p.stock}</p>
                </div>
                <p className="font-mono text-amber-400 text-sm">{inr(p.price)}</p>
                <Plus size={16} className="text-amber-500" />
              </button>
            ))}
          </div>
        )}

        {/* Cart lines */}
        <div className="flex-1 overflow-y-auto">
          <h2 className="text-sm font-medium text-slate-300 mb-3 uppercase tracking-wider">Current bill</h2>
          {cart.length === 0 ? (
            <div className="border border-dashed border-slate-800 rounded-md py-16 text-center text-slate-600">
              <ScanLine size={36} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">Scan or search products to start a bill</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="pos-cart">
              {cart.map((it, i) => (
                <div key={i} className="bg-slate-900 border border-slate-800 rounded-md p-3 flex flex-wrap items-center gap-3">
                  <img src={it.image} alt="" className="h-14 w-14 object-cover rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-100 truncate">{it.name}</p>
                    <p className="text-xs text-slate-500 font-mono">SKU {it.sku || "—"}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <input
                        value={it.size}
                        onChange={(e) => updateLine(i, { size: e.target.value })}
                        placeholder="Size"
                        className="w-14 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 font-mono"
                      />
                      {(it.colors && it.colors.length > 0) ? (
                        <select
                          value={it.color}
                          onChange={(e) => updateLine(i, { color: e.target.value })}
                          className="w-28 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200"
                          data-testid={`pos-line-color-${i}`}
                        >
                          {it.colors.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : (
                        <input
                          value={it.color}
                          onChange={(e) => updateLine(i, { color: e.target.value })}
                          placeholder="Color"
                          className="w-24 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200"
                        />
                      )}
                      <input
                        type="number"
                        value={it.line_discount}
                        onChange={(e) => updateLine(i, { line_discount: Number(e.target.value) || 0 })}
                        placeholder="Line ₹ off"
                        className="w-24 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 font-mono"
                        title="Discount on this line (₹)"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-auto w-full sm:w-auto justify-end">
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateLine(i, { qty: Math.max(1, it.qty - 1) })} className="grid place-items-center h-7 w-7 border border-slate-700 rounded text-slate-300 hover:border-amber-500"><Minus size={12} /></button>
                    <span className="w-8 text-center text-slate-100 font-mono">{it.qty}</span>
                    <button onClick={() => updateLine(i, { qty: it.qty + 1 })} className="grid place-items-center h-7 w-7 border border-slate-700 rounded text-slate-300 hover:border-amber-500"><Plus size={12} /></button>
                  </div>
                  <div className="w-28 text-right">
                    <input
                      type="number"
                      value={it.unit_price}
                      onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) || 0 })}
                      className="w-24 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-sm text-amber-400 font-mono text-right"
                    />
                    <p className="text-xs text-slate-500 mt-1 font-mono">= {inr(it.unit_price * it.qty - (it.line_discount || 0))}</p>
                  </div>
                  <button onClick={() => removeLine(i)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: totals & payment */}
      <div className="p-6 bg-slate-900 flex flex-col min-h-screen">
        {/* Customer */}
        <section className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2"><User size={13} /> Customer</h3>
            {customer && (
              <button onClick={() => setCustomer(null)} className="text-xs text-slate-500 hover:text-amber-400">Clear</button>
            )}
          </div>
          {customer ? (
            <div className="bg-slate-950 border border-amber-500/30 rounded p-3">
              <p className="text-sm text-slate-100">{customer.name}</p>
              <p className="text-xs text-slate-400 font-mono">{customer.phone}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                value={custQuery}
                onChange={(e) => setCustQuery(e.target.value)}
                placeholder="Search saved customer by name/phone"
                className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600"
                data-testid="pos-cust-search"
              />
              {custResults.length > 0 && (
                <div className="bg-slate-950 border border-slate-800 rounded max-h-32 overflow-y-auto divide-y divide-slate-800">
                  {custResults.map((c) => (
                    <button key={c.id} onClick={() => { setCustomer(c); setCustQuery(""); setCustResults([]); }} className="w-full p-2 text-left hover:bg-slate-800 text-xs">
                      <span className="text-slate-100">{c.name}</span>
                      <span className="text-slate-500 font-mono ml-2">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <input value={walkin.name} onChange={onWalkinName} list="walkin-name-suggestions" placeholder="Customer name" className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600" data-testid="pos-walkin-name" autoComplete="off" />
                <input value={walkin.phone} onChange={(e) => setWalkin({ ...walkin, phone: e.target.value })} placeholder="Phone" className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 font-mono placeholder:text-slate-600" data-testid="pos-walkin-phone" />
                <datalist id="walkin-name-suggestions">
                  {allCustomers.map((c) => <option key={c.id} value={c.name}>{c.phone ? c.phone : ""}</option>)}
                </datalist>
              </div>
              {walkin.name && walkin.phone && (
                <button
                  onClick={async () => {
                    const { data } = await api.post("/pos/customers", walkin);
                    setCustomer(data); setWalkin({ name: "", phone: "" });
                    loadCustomers();
                    toast.success("Customer saved");
                  }}
                  className="text-xs text-amber-400 hover:text-amber-300"
                >
                  + Save to customer database
                </button>
              )}
            </div>
          )}
        </section>

        {/* Discount */}
        <section className="mb-5">
          <h3 className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2 mb-2"><Percent size={13} /> Discount</h3>
          <div className="grid grid-cols-3 gap-1 mb-2">
            {["none", "flat", "percent"].map((t) => (
              <button
                key={t}
                onClick={() => setDiscountType(t)}
                className={`py-1.5 text-xs rounded ${discountType === t ? "bg-amber-500 text-slate-950 font-medium" : "bg-slate-950 text-slate-400 border border-slate-800"}`}
                data-testid={`pos-discount-${t}`}
              >
                {t === "none" ? "None" : t === "flat" ? "₹ Flat" : "% Off"}
              </button>
            ))}
          </div>
          {discountType !== "none" && (
            <input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} placeholder={discountType === "flat" ? "Amount in ₹" : "Percentage"} className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 font-mono" />
          )}
          <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code (optional)" className="w-full mt-2 bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 uppercase font-mono placeholder:text-slate-600" />
        </section>

        {/* GST */}
        <section className="mb-5">
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-2 cursor-pointer">
            <input type="checkbox" checked={gstEnabled} onChange={(e) => setGstEnabled(e.target.checked)} data-testid="pos-gst-toggle" />
            Apply GST
          </label>
          {gstEnabled && (
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={gstRate} onChange={(e) => setGstRate(e.target.value)} placeholder="GST %" className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 font-mono" />
              <input value={gstin} onChange={(e) => setGstin(e.target.value)} placeholder="Customer GSTIN" className="bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 uppercase font-mono" />
            </div>
          )}
        </section>

        {/* Totals */}
        <section className="border-t border-slate-800 py-4 space-y-1.5 text-sm font-mono">
          <div className="flex justify-between text-slate-400"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
          {discount > 0 && <div className="flex justify-between text-amber-400"><span>Discount</span><span>− {inr(discount)}</span></div>}
          {gstEnabled && (
            <>
              <div className="flex justify-between text-slate-500"><span>CGST ({gstRate / 2}%)</span><span>{inr(cgst)}</span></div>
              <div className="flex justify-between text-slate-500"><span>SGST ({gstRate / 2}%)</span><span>{inr(sgst)}</span></div>
            </>
          )}
          <div className="flex justify-between text-2xl text-slate-100 font-semibold pt-2 border-t border-slate-800">
            <span>TOTAL</span>
            <span data-testid="pos-total">{inr(total)}</span>
          </div>
        </section>

        {/* Payment split */}
        <section className="border-t border-slate-800 py-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2"><Wallet size={13} /> Payment</h3>
            <button onClick={payFull} className="text-xs text-amber-400 hover:text-amber-300">Pay full</button>
          </div>
          <div className="space-y-1.5">
            {payments.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={p.mode} onChange={(e) => setPayLine(i, { mode: e.target.value })} className="bg-slate-950 border border-slate-800 rounded px-2 py-2 text-xs text-slate-200 uppercase font-mono">
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input type="number" value={p.amount} onChange={(e) => setPayLine(i, { amount: e.target.value })} placeholder="Amount" className="flex-1 bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-200 font-mono" data-testid={`pos-pay-${i}`} />
                {payments.length > 1 && <button onClick={() => rmPayLine(i)} className="text-slate-500 hover:text-red-400"><X size={14} /></button>}
              </div>
            ))}
            <button onClick={addPayLine} className="text-xs text-slate-500 hover:text-amber-400">+ Split payment</button>
          </div>
          <div className="mt-3 flex justify-between text-xs font-mono">
            <span className="text-slate-500">Paid: <span className="text-slate-200">{inr(paid)}</span></span>
            <span className={balance > 0 ? "text-amber-400" : "text-emerald-400"}>Balance: {inr(balance)}</span>
          </div>
        </section>

        <label className="flex items-center gap-2 mb-3 text-sm text-slate-300 cursor-pointer select-none" data-testid="pos-booking-toggle">
          <input type="checkbox" checked={isBooking} onChange={(e) => setIsBooking(e.target.checked)} className="accent-amber-500 h-4 w-4" />
          Advance / Booking order <span className="text-xs text-slate-500">(deposit now, balance on delivery)</span>
        </label>

        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-300 placeholder:text-slate-600 mb-3 resize-none" />

        <div className="flex gap-2 mt-auto">
          <button
            disabled={saving || cart.length === 0}
            onClick={saveBill}
            className="flex-1 py-3.5 bg-slate-800 hover:bg-slate-700 rounded-md text-slate-100 text-sm font-medium disabled:opacity-40"
            data-testid="pos-save-bill"
          >
            {saving ? "Generating…" : (savedBill && cart.length === 0) ? "Generated ✓" : "Generate Bill"}
          </button>
          <button
            disabled={saving || cart.length === 0}
            onClick={saveAndPrint}
            className="flex-1 py-3.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-md font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="pos-save-print"
          >
            <Printer size={15} /> Generate &amp; Print
          </button>
        </div>
        {savedBill && cart.length === 0 && (savedBill.customer_phone || "").replace(/\D/g, "") && (
          <button
            onClick={() => waSendTerminal(savedBill)}
            className="mt-2 w-full py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-white rounded-md font-medium text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="pos-whatsapp-last"
          >
            <MessageCircle size={15} /> WhatsApp invoice for {savedBill.bill_no}
          </button>
        )}
      </div>

      {showPrintable && savedBill && (
        <PrintableBill bill={savedBill} settings={settings} />
      )}

      {/* Browse products modal */}
      {browseOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-3 sm:p-6" onClick={() => setBrowseOpen(false)} data-testid="pos-browse-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg max-w-5xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-800 flex flex-wrap items-center gap-3">
              <Grid3x3 className="text-amber-500" size={20} />
              <h2 className="text-lg font-semibold text-slate-100 flex-1">Browse catalogue</h2>
              <div className="relative w-full sm:w-72 order-last sm:order-none">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  value={browseQuery}
                  onChange={(e) => setBrowseQuery(e.target.value)}
                  placeholder="Filter by name, SKU, category"
                  className="w-full bg-slate-950 border border-slate-800 rounded pl-9 pr-3 py-2 text-sm text-slate-200"
                  data-testid="pos-browse-filter"
                />
              </div>
              <button onClick={() => setBrowseOpen(false)} className="text-slate-400 hover:text-slate-100"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {browseLoading ? (
                <p className="text-center py-16 text-slate-500 text-sm">Loading catalogue…</p>
              ) : filteredBrowse.length === 0 ? (
                <p className="text-center py-16 text-slate-500 text-sm">No products match "{browseQuery}"</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredBrowse.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => pickFromBrowse(p)}
                      className="text-left bg-slate-950 hover:bg-slate-800 border border-slate-800 hover:border-amber-500 rounded p-2 transition-colors"
                      data-testid={`pos-browse-item-${p.sku || p.slug}`}
                    >
                      <div className="aspect-square bg-slate-900 mb-2 overflow-hidden rounded">
                        <img src={p.primary_image} alt="" className="w-full h-full object-cover" />
                      </div>
                      <p className="text-xs text-slate-100 truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-500 font-mono truncate">{p.sku || "no SKU"}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-amber-400 font-mono">₹{inr(p.price)}</p>
                        <p className={`text-[10px] font-mono ${p.stock <= 3 ? "text-red-400" : "text-slate-500"}`}>stock {p.stock}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick add modal (temp / new item, no SKU, no stock decrement) */}
      {quickOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-3 sm:p-6" onClick={() => setQuickOpen(false)} data-testid="pos-quick-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-800 flex items-center gap-3">
              <Zap className="text-amber-500" size={20} />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-slate-100">Quick add</h2>
                <p className="text-xs text-slate-500">For unlisted or temporary items. Won't touch inventory.</p>
              </div>
              <button onClick={() => setQuickOpen(false)} className="text-slate-400 hover:text-slate-100"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Item name</span>
                <input
                  autoFocus
                  value={quick.name}
                  onChange={(e) => setQuick({ ...quick, name: e.target.value })}
                  placeholder="e.g. Alteration charges, gift wrap, custom saree"
                  className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200"
                  data-testid="pos-quick-name"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Price (₹)</span>
                  <input
                    type="number"
                    value={quick.price}
                    onChange={(e) => setQuick({ ...quick, price: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 font-mono"
                    data-testid="pos-quick-price"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Qty</span>
                  <input
                    type="number"
                    min={1}
                    value={quick.qty}
                    onChange={(e) => setQuick({ ...quick, qty: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2.5 text-sm text-slate-200 font-mono"
                    data-testid="pos-quick-qty"
                  />
                </label>
              </div>
              <button
                onClick={addQuick}
                className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded font-medium text-sm"
                data-testid="pos-quick-submit"
              >
                Add to bill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parked bills modal */}
      {parkedOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-3 sm:p-6" onClick={() => setParkedOpen(false)} data-testid="pos-parked-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListChecks className="text-amber-500" size={20} />
                <h2 className="text-lg font-semibold text-slate-100">Parked bills</h2>
              </div>
              <button onClick={() => setParkedOpen(false)} className="text-slate-400 hover:text-slate-100"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-2">
              {parked.length === 0 && <p className="text-center text-slate-500 text-sm py-8">No held bills right now.</p>}
              {parked.map((p) => (
                <div key={p.id} className="flex items-center gap-3 bg-slate-950 border border-slate-800 rounded p-3" data-testid={`pos-parked-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-100 text-sm truncate">{p.label}</p>
                    <p className="text-xs text-slate-500 font-mono">{p.count} item{p.count !== 1 ? "s" : ""} · {inr(p.total)} · {new Date(p.created_at || p.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  <button onClick={() => resumeBill(p.id)} className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded text-xs font-semibold" data-testid={`pos-resume-${p.id}`}>Resume</button>
                  <button onClick={() => deleteParked(p.id)} className="text-slate-500 hover:text-red-400" title="Discard"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
